import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import httpRequest from '../utils/request';

const Ecommerce = () => {
    const navigate = useNavigate();
    const { tenant } = useSelector((state) => state.tenant);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('products'); // 'orders', 'products'
    const [orders, setOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(false);
    const [selectedOrders, setSelectedOrders] = useState(new Set());
    const [processingOrder, setProcessingOrder] = useState(null);
    const [processingBatch, setProcessingBatch] = useState(false);

    // Estados Tienda Propia
    const [ownShopSettings, setOwnShopSettings] = useState({
        whatsapp: '',
        address: '',
        phone: '',
        businessHours: '',
        shippingZones: [],
        pickupPoints: [],
        pickupEnabled: true,
        deliveryEnabled: true,
        mercadoPago: { accessToken: '', publicKey: '', enabled: false },
        showOutOfStock: true,
        brandFont: 'Inter',
        logoUrl: '',
        bannerUrl: '',
        minPurchaseAmount: 0
    });
    const [showOwnShopConfig, setShowOwnShopConfig] = useState(false);
    const [removeBg] = useState(false); // Checkbox quitar fondo (unused setter removed)

    // Estados existentes
    const [config, setConfig] = useState(null);
    const [mappings, setMappings] = useState([]);
    const [products, setProducts] = useState([]);
    const [showMappingModal, setShowMappingModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [externalId, setExternalId] = useState('');
    const [externalSku, setExternalSku] = useState('');
    const [externalUrl, setExternalUrl] = useState('');
    const [syncing, setSyncing] = useState(false);
    const [priceMarkup, setPriceMarkup] = useState(0);
    const [showBulkImportModal, setShowBulkImportModal] = useState(false);
    const [externalItems, setExternalItems] = useState([]);
    const [loadingMlItems, setLoadingMlItems] = useState(false);
    const [bulkMappings, setBulkMappings] = useState([]);
    const [mappingPlatform, setMappingPlatform] = useState('mercadolibre');
    // selectedMappings removed (unused)
    const [clientId, setClientId] = useState('');
    const [clientSecret, setClientSecret] = useState('');
    const [puntosVenta, setPuntosVenta] = useState([]);
    const [defaultPv, setDefaultPv] = useState(null);
    const [systemDefaultPv, setSystemDefaultPv] = useState(null);
    // productFilter removed (unused)
    const [rowSearch, setRowSearch] = useState({}); // { externalId: 'typed text' }
    
    // Estado para Mapeo Automático
    const [autoMatching, setAutoMatching] = useState(false);
    const [showAutoMatchModal, setShowAutoMatchModal] = useState(false);
    const [autoMatchReport, setAutoMatchReport] = useState(null);

    // Estados para Historial de Sincronización
    const [syncLogs, setSyncLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const [logsPlatform, setLogsPlatform] = useState('');
    const [logsStatus, setLogsStatus] = useState('');
    const [logsPage, setLogsPage] = useState(1);
    const [logsTotalPages, setLogsTotalPages] = useState(1);

    // Estados para Estadísticas de E-commerce
    const [stats, setStats] = useState({
        mappedProductsCount: 0,
        totalProductsCount: 0,
        salesTotalAmount: 0,
        salesCount: 0,
        syncSuccessRate: 100,
        totalLogs: 0
    });
    const [, setStatsLoading] = useState(false);

    const loadStats = React.useCallback(async () => {
        try {
            setStatsLoading(true);
            const res = await httpRequest.get('/ecommerce/stats');
            if (res.data?.ok) {
                setStats({
                    mappedProductsCount: res.data.mappedProductsCount || 0,
                    totalProductsCount: res.data.totalProductsCount || 0,
                    salesTotalAmount: res.data.salesTotalAmount || 0,
                    salesCount: res.data.salesCount || 0,
                    syncSuccessRate: res.data.syncSuccessRate !== undefined ? res.data.syncSuccessRate : 100,
                    totalLogs: res.data.totalLogs || 0
                });
            }
        } catch (error) {
            console.error('Error cargando estadísticas de e-commerce:', error);
        } finally {
            setStatsLoading(false);
        }
    }, []);

    const loadSyncLogs = React.useCallback(async () => {
        try {
            setLogsLoading(true);
            const params = {
                page: logsPage,
                limit: 15,
                platform: logsPlatform || undefined,
                status: logsStatus || undefined
            };
            const queryString = Object.keys(params)
                .filter(k => params[k] !== undefined)
                .map(k => `${k}=${params[k]}`)
                .join('&');
            
            const res = await httpRequest.get(`/ecommerce/sync-logs?${queryString}`);
            if (res.data?.ok) {
                setSyncLogs(res.data.logs || []);
                setLogsTotalPages(res.data.pagination?.pages || 1);
            }
        } catch (error) {
            console.error('Error cargando logs de sincronización:', error);
        } finally {
            setLogsLoading(false);
        }
    }, [logsPage, logsPlatform, logsStatus]);

    useEffect(() => {
        if (activeTab === 'logs') {
            loadSyncLogs();
        }
    }, [activeTab, loadSyncLogs]);

    const loadOrders = React.useCallback(async () => {
        // Usamos hasCredentials porque el backend no devuelve el accessToken por seguridad
        if (!config?.hasCredentials?.mercadolibre) return;

        try {
            setLoadingOrders(true);
            const res = await httpRequest.get('/ecommerce/mercadolibre/orders');
            setOrders(res.data?.results || []);
        } catch (error) {
            console.error('Error cargando pedidos:', error);
        } finally {
            setLoadingOrders(false);
        }
    }, [config]);





    useEffect(() => {
        if (activeTab === 'orders' && config?.credentials?.mercadolibre) {
            loadOrders();
        }
    }, [activeTab, config, loadOrders]);

    const loadData = React.useCallback(async () => {
        try {
            setLoading(true);

            // Cargar configuración del módulo
            const configRes = await httpRequest.get('/ecommerce/config');
            setConfig(configRes.data);

            // Cargar estadísticas de e-commerce
            loadStats();
            if (configRes.data.ownShop) {
                setOwnShopSettings({
                    whatsapp: configRes.data.ownShop.whatsapp || '',
                    address: configRes.data.ownShop.address || '',
                    phone: configRes.data.ownShop.phone || '',
                    businessHours: configRes.data.ownShop.businessHours || '',
                    shippingZones: configRes.data.ownShop.shippingZones || [],
                    pickupPoints: configRes.data.ownShop.pickupPoints || [],
                    pickupEnabled: configRes.data.ownShop.pickupEnabled !== false,
                    deliveryEnabled: configRes.data.ownShop.deliveryEnabled !== false,
                    mercadoPago: configRes.data.ownShop.mercadoPago || { accessToken: '', publicKey: '', enabled: false },
                    subdomain: configRes.data.ownShop.subdomain || '',
                    customDomain: configRes.data.ownShop.customDomain || '',
                    domainVerified: configRes.data.ownShop.domainVerified || false,
                    showOutOfStock: configRes.data.ownShop.showOutOfStock !== false, // Corregido: por defecto true si no existe
                    brandColor: configRes.data.ownShop.brandColor || '#00CED1',
                    headerColor: configRes.data.ownShop.headerColor || '#00CED1',
                    buttonColor: configRes.data.ownShop.buttonColor || '#3483fa',
                    cartButtonColor: configRes.data.ownShop.cartButtonColor || '#3483fa',
                    brandFont: configRes.data.ownShop.brandFont || 'Inter',
                    logoUrl: configRes.data.ownShop.logoUrl || '',
                    bannerUrl: configRes.data.ownShop.bannerUrl || '',
                    minPurchaseAmount: configRes.data.ownShop.minPurchaseAmount || 0
                });
            }

            if (configRes.data.settings?.defaultSalesPoint) {
                setDefaultPv(configRes.data.settings.defaultSalesPoint);
            }

            // ... (unchanged mappings/products loading) ...
            const mappingsRes = await httpRequest.get('/ecommerce/mappings');
            setMappings(mappingsRes.data);
            const productsRes = await httpRequest.get('/product/all-products');
            setProducts(productsRes.data);

            // Cargar Fiscal Config para obtener Puntos de Venta
            try {
                const fiscalRes = await httpRequest.get('/fiscal/config');
                if (fiscalRes.data?.puntosVenta) {
                    const activePvs = fiscalRes.data.puntosVenta.filter(p => p.isActive);
                    setPuntosVenta(activePvs);

                    // Detectar cuál es el PV que usa el sistema por defecto (Lógica mirror del backend)
                    const sysDefault = activePvs.find(p => p.tipo === 'Electrónica') || activePvs[0];
                    if (sysDefault) {
                        setSystemDefaultPv(sysDefault.numero);
                    }
                }
            } catch (fiscalErr) {
                console.warn('No se pudo cargar config fiscal para PV selector', fiscalErr);
            }

            // ... (rest of function) ...

            // Inicializar markup local
            setPriceMarkup(configRes.data?.settings?.priceMarkupPercentage || 0);

            // Cargar credenciales si existen
            if (configRes.data?.credentials?.mercadolibre) {
                setClientId(configRes.data.credentials.mercadolibre.clientId || '');
                // No cargamos el secret por seguridad, solo mostramos que existe
            }

        } catch (error) {

            // Si el error es 403 (módulo no habilitado), mostrar mensaje especial
            if (error.response?.status === 403) {
                toast.error('El módulo E-commerce no está disponible en tu plan');
                navigate('/');
            } else {
                toast.error('Error al cargar datos del módulo');
            }
        } finally {
            setLoading(false);
        }
    }, [navigate, loadStats]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const tiendanubeStatus = urlParams.get('tiendanube');
        if (tiendanubeStatus === 'success') {
            toast.success('¡Tiendanube conectada exitosamente!');
            window.history.replaceState({}, document.title, window.location.pathname);
        } else if (tiendanubeStatus === 'error') {
            toast.error('Hubo un error al conectar Tiendanube');
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, []);

    // handleLogoUpload kept for potential future use
    const handleLogoUpload = async (e) => {
        let file = e.target.files[0];
        if (!file) return;

        // IA Remove Background (Client-Side)
        if (removeBg) {
            const toastId = toast.info('🤖 IA procesando imagen (quitando fondo)... Esto puede tardar unos segundos.', { autoClose: false });
            try {
                // Carga dinámica de la librería (lazy load para no afectar carga inicial)
                const module = await import('@imgly/background-removal');
                // La librería exporta removeBackground a veces como default, a veces named. Prevenimos ambos.
                const removeBackground = module.default || module.removeBackground;

                const blob = await removeBackground(file);
                file = new File([blob], file.name.replace(/\.[^/.]+$/, "") + ".png", { type: 'image/png' });

                toast.dismiss(toastId);
                toast.success('✨ Fondo eliminado con éxito');
            } catch (iaErr) {
                console.error('Error IA Remove BG:', iaErr);
                toast.dismiss(toastId);
                toast.warn('⚠️ No se pudo quitar el fondo, subiendo imagen original...');
            }
        }

        const formData = new FormData();
        formData.append('logo', file);

        try {
            toast.info('Subiendo logo...', { autoClose: 1000 });
            const res = await httpRequest.post('/ecommerce/upload-logo', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            if (res.data.url) {
                const logoUrl = res.data.url;
                setOwnShopSettings(prev => ({ ...prev, logoUrl }));
                // Auto-save ONLY the logoUrl field to DB (avoids stale closure issue)
                await httpRequest.put('/ecommerce/config', { 
                    ownShop: { logoUrl } 
                });
                toast.success('✅ Logo subido y guardado');
            }
        } catch (error) {
            console.error('Error al subir logo:', error);
            toast.error('Error al subir el logo');
        }
    };

    const handleBannerUpload = async (e) => {
        let file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('banner', file);

        try {
            toast.info('Subiendo banner...', { autoClose: 1000 });
            const res = await httpRequest.post('/ecommerce/upload-banner', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            if (res.data.url) {
                const bannerUrl = res.data.url;
                setOwnShopSettings(prev => ({ ...prev, bannerUrl }));
                // Auto-save ONLY the bannerUrl field to DB (avoids stale closure issue)
                await httpRequest.put('/ecommerce/config', { 
                    ownShop: { bannerUrl } 
                });
                toast.success('✅ Banner subido y guardado');
            }
        } catch (error) {
            console.error('Error al subir banner:', error);
            toast.error('Error al subir el banner');
        }
    };

    // Redirigir si el usuario está en una pestaña no permitida
    useEffect(() => {
        if (config) {
            const isMLOff = !config.platforms?.includes('mercadolibre');

            // Si el usuario intenta entrar a Pedidos pero no es Pro o ML está apagado
            if ((config.proEnabled === false || isMLOff) && activeTab === 'orders') {
                setActiveTab('products');
            }
        }
    }, [config, activeTab]);

    const handleConnectMercadoLibre = async () => {
        try {
            // Validar que se hayan ingresado las credenciales
            if (!clientId || !clientSecret) {
                toast.error('Por favor ingresa Client ID y Client Secret');
                return;
            }

            // Guardar credenciales primero
            const saveRes = await httpRequest.put('/ecommerce/config', {
                credentials: {
                    mercadolibre: {
                        clientId,
                        clientSecret
                    }
                }
            });

            console.log('Credenciales guardadas:', saveRes.data);
            toast.success('Credenciales guardadas. Iniciando conexión...');

            // Delay más largo para asegurar que la DB se actualice
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Ahora iniciar el flujo OAuth
            const res = await httpRequest.get('/ecommerce/mercadolibre/auth');


            const { authUrl } = res.data;

            if (!authUrl) {
                throw new Error('No se recibió URL de autorización');
            }

            // Redirigir a Mercado Libre para autorización
            window.location.href = authUrl;
        } catch (error) {
            console.error('Error iniciando conexión:', error);
            console.error('Error details:', error.response?.data);

            const errorMessage = error.response?.data?.message || 'Error al iniciar conexión con Mercado Libre';
            toast.error(errorMessage);
        }
    };

    const handleDisconnectMercadoLibre = async () => {
        if (!window.confirm('¿Estás seguro de desconectar Mercado Libre? Se eliminarán todos los mapeos.')) {
            return;
        }

        try {
            await httpRequest.delete('/ecommerce/mercadolibre/disconnect');
            toast.success('Mercado Libre desconectado');
            loadData();
        } catch (error) {
            console.error('Error desconectando:', error);
            toast.error('Error al desconectar Mercado Libre');
        }
    };

    const handleConnectTiendanube = async () => {
        try {
            const res = await httpRequest.get('/tiendanube/auth');
            if (res.data.authUrl) {
                window.location.href = res.data.authUrl;
            }
        } catch (error) {
            console.error('Error al conectar Tiendanube:', error);
            toast.error('Error al iniciar la vinculación con Tiendanube');
        }
    };

    const handleDisconnectTiendanube = async () => {
        if (!window.confirm('¿Estás seguro de desconectar Tiendanube? Se detendrá la sincronización de stock y precios.')) return;
        try {
            await httpRequest.delete('/tiendanube/disconnect');
            toast.success('Tiendanube desconectado correctamente');
            loadData();
        } catch (error) {
            console.error('Error al desconectar Tiendanube:', error);
            toast.error('Error al desconectar Tiendanube');
        }
    };

    const toggleOrderSelection = (orderId) => {
        const next = new Set(selectedOrders);
        if (next.has(orderId)) next.delete(orderId);
        else next.add(orderId);
        setSelectedOrders(next);
    };

    const toggleSelectAll = () => {
        // Seleccionar solo los no facturados
        const available = orders.filter(o => o.local_status !== 'invoiced');

        if (selectedOrders.size === available.length && available.length > 0) {
            setSelectedOrders(new Set());
        } else {
            setSelectedOrders(new Set(available.map(o => o.id)));
        }
    };

    const handleBatchInvoice = async () => {
        if (selectedOrders.size === 0) return;
        if (!window.confirm(`¿Generar factura para ${selectedOrders.size} pedidos seleccionados?\n(Esto puede tomar unos momentos)`)) return;

        setProcessingBatch(true);
        const ids = Array.from(selectedOrders);
        let successCount = 0;
        let failCount = 0;

        let lastError = '';

        try {
            for (const orderId of ids) {
                setProcessingOrder(orderId);
                try {
                    const res = await httpRequest.post(`/ecommerce/mercadolibre/orders/${orderId}/create-sale`, { forceInvoice: true });
                    if (res.data.invoice) successCount++;
                    // Si retorna afipError cuenta como fallo parcial
                    else {
                        failCount++;
                        if (res.data.afipError) lastError = res.data.afipError;
                        else if (res.data.message) lastError = res.data.message;
                        else lastError = 'No se generó factura';
                    }
                } catch (e) {
                    console.error(`Error facturando orden ${orderId}`, e);
                    failCount++;
                    lastError = e.response?.data?.message || e.message;
                }
            }
            if (failCount > 0) {
                toast.warning(`Finalizado. ✅ ${successCount} ok. ⚠️ ${failCount} fallidos. Razón: ${lastError}`, { autoClose: 10000 });
            } else {
                toast.success(`Finalizado. ✅ ${successCount} facturados correctamente.`);
            }
        } finally {
            setProcessingOrder(null);
            setProcessingBatch(false);
            setSelectedOrders(new Set());
            loadOrders();
        }
    };

    const handleSendInvoice = async (orderId) => {
        if (!window.confirm('¿Enviar factura por mensaje de Mercado Libre al comprador?')) return;

        try {
            setProcessingOrder(orderId);
            toast.info('📤 Generando PDF y enviando...');
            await httpRequest.post(`/ecommerce/mercadolibre/orders/${orderId}/send-invoice`);
            toast.success('✅ Factura enviada por mensajería correctamente');
        } catch (error) {
            console.error('Error enviando factura:', error);
            toast.error(error.response?.data?.message || 'Error al enviar factura');
        } finally {
            setProcessingOrder(null);
        }
    };

    const handleCreateMapping = async () => {
        if (!selectedProduct || !externalId) {
            toast.warning(`Selecciona un producto e ingresa el ID de ${mappingPlatform === 'mercadolibre' ? 'Mercado Libre' : 'Tiendanube'}`);
            return;
        }

        try {
            await httpRequest.post('/ecommerce/mappings', {
                productId: selectedProduct,
                platform: mappingPlatform,
                externalId,
                externalSku,
                externalUrl
            });

            toast.success('Producto vinculado exitosamente');
            setShowMappingModal(false);
            setSelectedProduct(null);
            setExternalId('');
            setExternalSku('');
            setExternalUrl('');
            loadData();
        } catch (error) {
            console.error('Error creando mapeo:', error);
            toast.error('Error al vincular producto');
        }
    };

    const handleAutoMatch = async (platform) => {
        if (!window.confirm(`¿Deseas iniciar el mapeo automático express para ${platform === 'tiendanube' ? 'Tiendanube' : 'Mercado Libre'}?\n\nEl sistema buscará coincidencias exactas por SKU y Código de Barras entre tus productos locales y la tienda online.`)) return;
        
        try {
            setAutoMatching(true);
            const res = await httpRequest.post('/ecommerce/mappings/auto-match', { platform });
            
            if (res.data?.ok) {
                setAutoMatchReport(res.data);
                setShowAutoMatchModal(true);
                toast.success('¡Proceso de mapeo automático finalizado!');
                loadData();
            } else {
                toast.error(res.data?.message || 'Error en el mapeo automático');
            }
        } catch (error) {
            console.error('Error auto matching:', error);
            toast.error(error.response?.data?.message || 'Error al procesar mapeo automático');
        } finally {
            setAutoMatching(false);
        }
    };

    const handleDeleteMapping = async (mappingId) => {
        if (!window.confirm('¿Eliminar este mapeo?')) return;

        try {
            await httpRequest.delete(`/ecommerce/mappings/${mappingId}`);
            toast.success('Mapeo eliminado');
            loadData();
        } catch (error) {
            console.error('Error eliminando mapeo:', error);
            toast.error('Error al eliminar mapeo');
        }
    };

    const handleSyncStock = async (productId, platform = 'mercadolibre') => {
        try {
            setSyncing(true);
            await httpRequest.post(`/ecommerce/sync/stock/${productId}`, {
                platform
            });
            toast.success('Stock sincronizado');
            loadData();
        } catch (error) {
            console.error('Error sincronizando:', error);
            toast.error('Error al sincronizar stock');
        } finally {
            setSyncing(false);
        }
    };

    const handleSyncPrice = async (productId, platform = 'mercadolibre') => {
        try {
            setSyncing(true);
            await httpRequest.post(`/ecommerce/sync/price/${productId}`, {
                platform
            });
            toast.success('Precio sincronizado');
            loadData();
        } catch (error) {
            console.error('Error sincronizando:', error);
            toast.error('Error al sincronizar precio');
        } finally {
            setSyncing(false);
        }
    };

    const handleBulkImport = async (platform = 'mercadolibre') => {
        try {
            setMappingPlatform(platform);
            setLoadingMlItems(true);
            setShowBulkImportModal(true);

            let url = '/ecommerce/mercadolibre/items';
            if (platform === 'tiendanube') {
                url = '/tiendanube/items';
            }

            const res = await httpRequest.get(url);
            let data = res.data.items || [];

            // Filtrar productos que ya están mapeados para esta plataforma
            const mappedExternalIds = mappings
                .filter(m => m.platform === platform)
                .map(m => String(m.externalId));

            const filteredData = data.filter(item => !mappedExternalIds.includes(String(item.id)));

            if (filteredData.length === 0 && data.length > 0) {
                toast.info(`Todos los productos de ${platform === 'tiendanube' ? 'Tiendanube' : 'Mercado Libre'} ya están vinculados`);
            } else if (filteredData.length === 0) {
                toast.info(`No se encontraron publicaciones activas en ${platform === 'tiendanube' ? 'Tiendanube' : 'Mercado Libre'}`);
            }

            setExternalItems(filteredData);
            setBulkMappings([]); // Limpiar selecciones previas
        } catch (error) {
            console.error('❌ Error cargando items:', error);
            if (error.response?.data) {
                console.error('Detalles técnicos del error:', JSON.stringify(error.response.data, null, 2));
            }
            toast.error(error.response?.data?.message || `Error al cargar publicaciones de ${platform === 'tiendanube' ? 'Tiendanube' : 'Mercado Libre'}`);
            setShowBulkImportModal(false);
        } finally {
            setLoadingMlItems(false);
        }
    };

    const handleInvoiceOrder = async (orderId) => {
        if (!window.confirm('¿Desea generar la venta y factura para este pedido?')) return;

        try {
            setProcessingOrder(orderId);
            const res = await httpRequest.post(`/ecommerce/mercadolibre/orders/${orderId}/create-sale`, { forceInvoice: true });

            if (res.data.invoice) {
                toast.success('✅ Venta y Factura generadas correctamente');
            } else if (res.data.afipError) {
                // Mensaje largo pero necesario para que el usuario entienda
                toast.warning(`⚠️ Venta creada, pero falló Facturación AFIP: ${res.data.afipError}`, { autoClose: 8000 });
            } else {
                toast.success(res.data.message || 'Venta generada exitosamente');
            }

        } catch (error) {
            console.error(error);
            if (error.response?.data?.missing) {
                toast.warning(`Falta vincular productos: ${error.response.data.missing.join(', ')}`);
            } else {
                toast.error(error.response?.data?.message || 'Error al procesar pedido');
            }
        } finally {
            setProcessingOrder(null);
        }
    };

    const handleSaveBulkMappings = async () => {
        try {
            const mappingsArray = bulkMappings
                .filter(m => m.productId)
                .map(m => ({
                    productId: m.productId,
                    externalId: m.externalId,
                    platform: mappingPlatform
                }));

            if (mappingsArray.length === 0) {
                toast.warning('Selecciona al menos un producto para vincular');
                return;
            }

            const res = await httpRequest.post('/ecommerce/mappings/bulk', {
                mappings: mappingsArray
            });

            toast.success(`${res.data.created} productos vinculados exitosamente`);
            if (res.data.errors && res.data.errors.length > 0) {
                toast.warning(`${res.data.errors.length} productos no pudieron vincularse`);
            }

            setShowBulkImportModal(false);
            setBulkMappings([]);
            loadData();
        } catch (error) {
            console.error('Error guardando mapeos:', error);
            toast.error('Error al vincular productos');
        }
    };

    const handlePrintLabel = async (orderId) => {
        try {
            toast.info('Generando etiqueta...');
            const response = await httpRequest.get(`/ecommerce/mercadolibre/orders/${orderId}/label`, {
                responseType: 'blob'
            });

            // Crear Blob y URL
            const file = new Blob([response.data], { type: 'application/pdf' });
            const fileURL = URL.createObjectURL(file);

            // Abrir en nueva ventana
            window.open(fileURL, '_blank');
        } catch (error) {
            console.error('Error al imprimir etiqueta:', error);
            const msg = error.response?.data?.message || 'Error al obtener la etiqueta de envío';
            toast.error(msg);
        }
    };

    // handleUpdateOwnShop kept for potential future use
    const handleUpdateOwnShop = async () => {
        try {
            await httpRequest.put('/ecommerce/config', { ownShop: ownShopSettings });
            toast.success('✅ Configuración de tienda guardada');
            // Do NOT call loadData() here - it would overwrite the local state
            // (including freshly-uploaded bannerUrl/logoUrl) with whatever is in DB.
            // Only reload mappings/products which are separate from ownShop settings.
        } catch (error) {
            console.error('Error actualizando tienda propia:', error);
            toast.error('Error al guardar configuración');
        }
    };

    const handleUpdateSettings = async (newSettings) => {
        try {
            await httpRequest.put('/ecommerce/config', { settings: newSettings });
            toast.success('Configuración actualizada');
            loadData();
        } catch (error) {
            console.error('Error actualizando configuración:', error);
            toast.error('Error al actualizar configuración');
        }
    };


    // handleResetVisits kept for potential future use
    const handleResetVisits = async () => {
        if (!window.confirm('¿Deseas poner a cero el contador de visitas de la tienda?')) return;
        try {
            await httpRequest.post('/ecommerce/reset-visits');
            toast.success('Contador de visitas reseteado');
            loadData();
        } catch (error) {
            console.error('Error reseteando visitas:', error);
            toast.error('Error al resetear contador');
        }
    };

    if (loading) {
        return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
                <div style={{ fontSize: '18px', color: '#666' }}>Cargando módulo E-commerce...</div>
            </div>
        );
    }

    if (!config) {
        return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
                <div style={{ fontSize: '18px', color: '#dc3545' }}>Error al cargar configuración</div>
            </div>
        );
    }

    const isOwnShopEnabled = config.ownShop?.enabled;
    const isMLPlatformEnabled = config.platforms?.includes('mercadolibre');
    const isTiendanubePlatformEnabled = config.platforms?.includes('tiendanube');
    const hasMercadoLibre = config.hasCredentials?.mercadolibre && isMLPlatformEnabled;
    const hasTiendanube = (config.credentials?.tiendanube?.enabled || config.hasCredentials?.tiendanube) && isTiendanubePlatformEnabled;

    return (
        <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
            {/* Header */}
            <div style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px' }}>
                        🛒 Integraciones E-commerce
                    </h1>
                    <p style={{ color: '#666', fontSize: '14px' }}>
                        Conecta tu sistema con plataformas de venta online
                    </p>
                </div>
                <button
                    onClick={() => navigate('/dashboard')}
                    style={{
                        padding: '8px 16px',
                        borderRadius: '6px',
                        border: '1px solid #ddd',
                        background: '#fff',
                        cursor: 'pointer',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: '#666'
                    }}
                >
                    ← Volver al Dashboard
                </button>
            </div>

            {/* KPI Statistics Dashboard Row */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '20px',
                marginBottom: '30px'
            }}>
                {/* Card 1: Ventas */}
                <div style={{
                    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                    border: '1px solid #bbf7d0',
                    borderRadius: '16px',
                    padding: '20px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.02)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    minHeight: '120px'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                            <span style={{ fontSize: '12px', fontWeight: '700', color: '#166534', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Ventas E-commerce (Mes)
                            </span>
                            <h2 style={{ fontSize: '26px', fontWeight: '900', color: '#14532d', margin: '8px 0 0 0' }}>
                                ${stats.salesTotalAmount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </h2>
                        </div>
                        <div style={{ fontSize: '24px', background: '#fff', padding: '8px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                            💰
                        </div>
                    </div>
                    <div style={{ fontSize: '13px', color: '#15803d', fontWeight: '600', marginTop: '10px' }}>
                        📈 {stats.salesCount} pedido(s) importados este mes
                    </div>
                </div>

                {/* Card 2: Mapeo */}
                <div style={{
                    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                    border: '1px solid #bfdbfe',
                    borderRadius: '16px',
                    padding: '20px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.02)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    minHeight: '120px'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                            <span style={{ fontSize: '12px', fontWeight: '700', color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Mapeo de Catálogo
                            </span>
                            <h2 style={{ fontSize: '26px', fontWeight: '900', color: '#1e3a8a', margin: '8px 0 0 0' }}>
                                {stats.totalProductsCount > 0 ? ((stats.mappedProductsCount / stats.totalProductsCount) * 100).toFixed(1) : 0}%
                            </h2>
                        </div>
                        <div style={{ fontSize: '24px', background: '#fff', padding: '8px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                            🔗
                        </div>
                    </div>
                    <div style={{ marginTop: '10px' }}>
                        <div style={{ fontSize: '13px', color: '#1d4ed8', fontWeight: '600', marginBottom: '6px' }}>
                            {stats.mappedProductsCount} de {stats.totalProductsCount} productos vinculados
                        </div>
                        <div style={{ width: '100%', height: '6px', background: 'rgba(255, 255, 255, 0.6)', borderRadius: '10px', overflow: 'hidden' }}>
                            <div style={{ 
                                width: `${stats.totalProductsCount > 0 ? (stats.mappedProductsCount / stats.totalProductsCount) * 100 : 0}%`, 
                                height: '100%', 
                                background: '#2563eb', 
                                borderRadius: '10px',
                                transition: 'width 0.5s ease-in-out'
                            }} />
                        </div>
                    </div>
                </div>

                {/* Card 3: Sincronización */}
                <div style={{
                    background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
                    border: '1px solid #e9d5ff',
                    borderRadius: '16px',
                    padding: '20px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.02)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    minHeight: '120px'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                            <span style={{ fontSize: '12px', fontWeight: '700', color: '#6b21a8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Tasa de Éxito Sync
                            </span>
                            <h2 style={{ fontSize: '26px', fontWeight: '900', color: '#581c87', margin: '8px 0 0 0' }}>
                                {stats.syncSuccessRate}%
                            </h2>
                        </div>
                        <div style={{ fontSize: '24px', background: '#fff', padding: '8px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                            ⚡
                        </div>
                    </div>
                    <div style={{ fontSize: '13px', color: '#7e22ce', fontWeight: '600', marginTop: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ 
                            display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', 
                            background: stats.syncSuccessRate >= 95 ? '#22c55e' : (stats.syncSuccessRate >= 80 ? '#eab308' : '#ef4444'),
                            boxShadow: stats.syncSuccessRate >= 95 ? '0 0 8px #22c55e' : 'none'
                        }} />
                        {stats.totalLogs} operaciones registradas (30d)
                    </div>
                </div>
            </div>

            {/* Tabs Navigation */}
            <div style={{ display: 'flex', gap: '30px', marginBottom: '30px', borderBottom: '1px solid #eee', paddingBottom: '0' }}>
                {(config.proEnabled !== false && isMLPlatformEnabled) && (
                    <button
                        onClick={() => setActiveTab('orders')}
                        style={{
                            padding: '10px 0',
                            background: 'none',
                            border: 'none',
                            fontSize: '16px',
                            fontWeight: activeTab === 'orders' ? '600' : '400',
                            color: activeTab === 'orders' ? '#2196f3' : '#666',
                            borderBottom: activeTab === 'orders' ? '3px solid #2196f3' : '3px solid transparent',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        📦 Últimos Pedidos de ML
                    </button>
                )}
                <button
                    onClick={() => setActiveTab('products')}
                    style={{
                        padding: '10px 0',
                        background: 'none',
                        border: 'none',
                        fontSize: '16px',
                        fontWeight: activeTab === 'products' ? '600' : '400',
                        color: activeTab === 'products' ? '#2196f3' : '#666',
                        borderBottom: activeTab === 'products' ? '3px solid #2196f3' : '3px solid transparent',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    🏷️ Catálogo y Configuración
                </button>
                <button
                    onClick={() => setActiveTab('logs')}
                    style={{
                        padding: '10px 0',
                        background: 'none',
                        border: 'none',
                        fontSize: '16px',
                        fontWeight: activeTab === 'logs' ? '600' : '400',
                        color: activeTab === 'logs' ? '#2196f3' : '#666',
                        borderBottom: activeTab === 'logs' ? '3px solid #2196f3' : '3px solid transparent',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    📋 Historial de Sincronización
                </button>
            </div>

            {/* Orders Tab */}
            {activeTab === 'orders' && config?.proEnabled !== false && isMLPlatformEnabled && (
                <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', alignItems: 'center' }}>
                        <h3 style={{ margin: 0 }}>Últimos Pedidos de Mercado Libre</h3>
                        {!isMLPlatformEnabled && <span style={{ color: '#dc3545', fontWeight: '600' }}>⚠️ Integración desactivada por administrador</span>}
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                onClick={toggleSelectAll}
                                disabled={loadingOrders || processingBatch}
                                style={{
                                    padding: '8px 16px',
                                    background: '#fff',
                                    border: '1px solid #007bff',
                                    color: '#007bff',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontWeight: '600'
                                }}
                            >
                                {orders.length > 0 && orders.filter(o => o.local_status !== 'invoiced').every(o => selectedOrders.has(o.id))
                                    ? 'Deseleccionar'
                                    : '✅ Seleccionar Pendientes'}
                            </button>
                            {selectedOrders.size > 0 && (
                                <button
                                    onClick={handleBatchInvoice}
                                    disabled={processingBatch}
                                    style={{
                                        padding: '8px 16px',
                                        background: '#28a745',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: processingBatch ? 'wait' : 'pointer',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {processingBatch ? 'Procesando...' : `⚡ Facturar (${selectedOrders.size})`}
                                </button>
                            )}
                            <button
                                onClick={loadOrders}
                                disabled={loadingOrders || processingBatch}
                                style={{ padding: '8px 16px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}
                            >
                                {loadingOrders ? 'Actualizando...' : '🔄 Actualizar'}
                            </button>
                        </div>
                    </div>

                    {!hasMercadoLibre ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                            <p>Conecta tu cuenta de Mercado Libre en la pestaña "Catálogo y Configuración" para ver tus pedidos.</p>
                        </div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #f0f0f0', textAlign: 'left', color: '#666' }}>
                                    <th style={{ padding: '12px', width: '50px', textAlign: 'center' }}>
                                        <input
                                            type="checkbox"
                                            title="Seleccionar Todos"
                                            style={{ transform: 'scale(1.5)', cursor: 'pointer' }}
                                            checked={orders.length > 0 && orders.filter(o => o.local_status !== 'invoiced').every(o => selectedOrders.has(o.id))}
                                            onChange={toggleSelectAll}
                                            disabled={loadingOrders || processingBatch}
                                        />
                                    </th>
                                    <th style={{ padding: '12px' }}>Fecha</th>
                                    <th style={{ padding: '12px' }}>Pedido #</th>
                                    <th style={{ padding: '12px' }}>Comprador</th>
                                    <th style={{ padding: '12px' }}>Productos</th>
                                    <th style={{ padding: '12px' }}>Total</th>
                                    <th style={{ padding: '12px' }}>Estado</th>
                                    <th style={{ padding: '12px' }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loadingOrders ? (
                                    <tr><td colSpan="8" style={{ padding: '40px', textAlign: 'center' }}>Cargando pedidos...</td></tr>
                                ) : orders.length === 0 ? (
                                    <tr><td colSpan="8" style={{ padding: '40px', textAlign: 'center' }}>No se encontraron pedidos recientes.</td></tr>
                                ) : (
                                    orders.map(order => (
                                        <tr
                                            key={order.id}
                                            style={{
                                                borderBottom: '1px solid #f0f0f0',
                                                backgroundColor: order.local_status === 'invoiced' ? '#f8fff9' : (selectedOrders.has(order.id) ? '#f0f9ff' : 'transparent'),
                                                cursor: order.local_status !== 'invoiced' ? 'pointer' : 'default'
                                            }}
                                            onClick={(e) => {
                                                // No disparar si se clickea en botones o links explícitos
                                                if (['BUTTON', 'A'].includes(e.target.tagName)) return;
                                                // Si es click en un input que NO es el checkbox de selección (p.ej. otro input futuro), ignorar.
                                                // Pero nuestro checkbox tiene pointer-events: none, así que nunca será target.
                                                if (order.local_status !== 'invoiced') toggleOrderSelection(order.id);
                                            }}
                                        >
                                            <td style={{ padding: '12px', textAlign: 'center' }}>
                                                {order.local_status !== 'invoiced' && (
                                                    <input
                                                        type="checkbox"
                                                        title="Seleccionar para facturar"
                                                        style={{ transform: 'scale(1.5)', cursor: 'pointer', pointerEvents: 'none' }}
                                                        checked={selectedOrders.has(order.id)}
                                                        readOnly
                                                    />
                                                )}
                                            </td>
                                            <td style={{ padding: '12px' }}>{new Date(order.date_created).toLocaleDateString()}</td>
                                            <td style={{ padding: '12px' }}>{order.id}</td>
                                            <td style={{ padding: '12px' }}>
                                                <div>{order.buyer.nickname}</div>
                                            </td>
                                            <td style={{ padding: '12px' }}>
                                                {order.order_items.map(item => (
                                                    <div key={item.item.id} style={{ fontSize: '13px' }}>
                                                        {item.quantity}x {item.item.title.substring(0, 30)}...
                                                    </div>
                                                ))}
                                            </td>
                                            <td style={{ padding: '12px', fontWeight: 'bold' }}>
                                                ${order.total_amount.toLocaleString()}
                                            </td>
                                            <td style={{ padding: '12px' }}>
                                                <span style={{
                                                    padding: '4px 8px', borderRadius: '4px', fontSize: '12px',
                                                    backgroundColor: order.status === 'paid' ? '#e8f5e9' : '#fff3e0',
                                                    color: order.status === 'paid' ? '#2e7d32' : '#f57c00'
                                                }}>
                                                    {order.status === 'paid' ? 'Pagado' : order.status}
                                                </span>
                                            </td>
                                            <td style={{ padding: '12px' }}>
                                                <div style={{ display: 'flex', gap: '4px' }}>
                                                    {(order.tags && order.tags.includes('no_shipping')) ? (
                                                        <span
                                                            title="Entrega sin envío (Retiro en tienda o Acordar con vendedor)"
                                                            style={{ fontSize: '20px', cursor: 'help' }}
                                                        >
                                                            🏪
                                                        </span>
                                                    ) : (order.shipping && order.shipping.id) ? (
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handlePrintLabel(order.id); }}
                                                            title="Imprimir Etiqueta de Envío"
                                                            style={{
                                                                padding: '6px 12px',
                                                                fontSize: '13px',
                                                                background: '#007bff',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '4px',
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                            }}
                                                        >
                                                            🖨️
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handlePrintLabel(order.id); }}
                                                            title={`No se detecta envío automático. Clic para forzar búsqueda. (Debug: ${JSON.stringify(order.shipping)})`}
                                                            style={{
                                                                padding: '6px 12px',
                                                                fontSize: '13px',
                                                                background: '#6c757d', // Gris
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '4px',
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center'
                                                            }}
                                                        >
                                                            🔍
                                                        </button>
                                                    )}

                                                    {order.local_status === 'invoiced' ? (
                                                        <div style={{ display: 'flex', gap: '4px' }}>
                                                            <button
                                                                disabled
                                                                style={{
                                                                    padding: '6px 12px',
                                                                    fontSize: '13px',
                                                                    background: 'transparent',
                                                                    color: '#28a745',
                                                                    border: '1px solid #28a745',
                                                                    borderRadius: '4px',
                                                                    cursor: 'default',
                                                                    opacity: 0.7
                                                                }}
                                                            >
                                                                ✓ Listo
                                                            </button>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); handleSendInvoice(order.id); }}
                                                                disabled={processingOrder === order.id}
                                                                title="Reenviar Factura por Mensaje de ML"
                                                                style={{
                                                                    padding: '6px 12px',
                                                                    fontSize: '13px',
                                                                    background: '#17a2b8',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    borderRadius: '4px',
                                                                    cursor: 'pointer'
                                                                }}
                                                            >
                                                                {processingOrder === order.id ? '...' : '📧'}
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleInvoiceOrder(order.id); }}
                                                            disabled={processingOrder === order.id || processingBatch}
                                                            style={{
                                                                padding: '6px 12px',
                                                                fontSize: '13px',
                                                                background: processingOrder === order.id ? '#ccc' : '#28a745',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '4px',
                                                                cursor: processingOrder === order.id ? 'not-allowed' : 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '4px'
                                                            }}
                                                        >
                                                            {processingOrder === order.id ? '...' : '🧾 Facturar'}
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            <div style={{ display: activeTab === 'products' ? 'block' : 'none' }}>
                {/* Plataformas */}
                <div style={{ marginBottom: '30px' }}>
                    <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px' }}>
                        Plataformas Disponibles
                    </h2>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px' }}>
                        {/* Tiendanube */}
                        {isTiendanubePlatformEnabled && (
                            <div style={{
                                border: '1px solid #e0e0e0',
                                borderRadius: '12px',
                                padding: '24px',
                                backgroundColor: '#fff',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                                    <div style={{
                                        width: '48px',
                                        height: '48px',
                                        borderRadius: '10px',
                                        background: 'linear-gradient(135deg, #1A1A1A 0%, #333333 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '24px',
                                        marginRight: '15px',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}>
                                        ☁️
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize: '18px', fontWeight: '700', margin: 0 }}>Tiendanube</h3>
                                        <div style={{ fontSize: '12px', marginTop: '2px' }}>
                                            {config.credentials?.tiendanube?.enabled ? (
                                                <span style={{ color: '#28a745', fontWeight: '600' }}>✓ Conectado (ID: {config.credentials?.tiendanube?.storeId || 'N/A'})</span>
                                            ) : (
                                                <span style={{ color: '#999' }}>No vinculado</span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '20px' }}>
                                    Sincroniza tu catálogo físico con tu Tiendanube.
                                </p>

                                <div style={{ marginBottom: '20px' }}>
                                    <div style={{
                                        padding: '12px',
                                        backgroundColor: '#f8fafc',
                                        borderRadius: '8px',
                                        border: '1px solid #e2e8f0',
                                        marginBottom: '10px'
                                    }}>
                                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            🔄 Sincronización Automática
                                        </div>
                                        <p style={{ fontSize: '12px', color: '#64748b', margin: '4px 0 0 24px' }}>
                                            Stock y Precios siempre actualizados.
                                        </p>
                                    </div>

                                    {config.credentials?.tiendanube?.enabled && (
                                        <div style={{
                                            padding: '12px',
                                            backgroundColor: config.proEnabled !== false ? '#f0fdf4' : '#f8fafc',
                                            borderRadius: '8px',
                                            border: config.proEnabled !== false ? '1px solid #bbf7d0' : '1px solid #e2e8f0'
                                        }}>
                                            <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                🧾 Opción 2: Facturación Automática
                                                {config.proEnabled === false && <span style={{ fontSize: '10px', backgroundColor: '#e2e8f0', padding: '2px 6px', borderRadius: '4px', marginLeft: 'auto' }}>PRO</span>}
                                            </div>
                                            <p style={{ fontSize: '12px', color: '#64748b', margin: '4px 0 0 24px' }}>
                                                Generación automática de facturas electrónicas AFIP para cada venta.
                                            </p>

                                            {config.proEnabled !== false && (
                                                <div style={{ marginTop: '12px', paddingLeft: '24px' }}>
                                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#334155', marginBottom: '4px' }}>
                                                        PUNTO DE VENTA EXCLUSIVO (AFIP)
                                                    </label>
                                                    <select
                                                        value={defaultPv || ''}
                                                        onChange={(e) => {
                                                            const val = e.target.value ? Number(e.target.value) : null;
                                                            setDefaultPv(val);
                                                            handleUpdateSettings({ ...config.settings, defaultSalesPoint: val });
                                                        }}
                                                        style={{
                                                            width: '100%',
                                                            padding: '8px',
                                                            border: '1px solid #cbd5e1',
                                                            borderRadius: '6px',
                                                            fontSize: '13px',
                                                            backgroundColor: '#fff'
                                                        }}
                                                    >
                                                        <option value="">Seleccionar PV...</option>
                                                        {puntosVenta
                                                            .filter(pv => pv.numero !== systemDefaultPv)
                                                            .map(pv => (
                                                                <option key={pv.numero} value={pv.numero}>
                                                                    {String(pv.numero).padStart(5, '0')} - {pv.tipo}
                                                                </option>
                                                            ))
                                                        }
                                                    </select>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div style={{ marginTop: 'auto' }}>
                                    {config.credentials?.tiendanube?.enabled ? (
                                        <>
                                            <button
                                                onClick={handleDisconnectTiendanube}
                                                style={{ width: '100%', padding: '12px', backgroundColor: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', marginBottom: '10px' }}
                                            >
                                                Desconectar Cuenta
                                            </button>
                                            <button
                                                onClick={() => handleBulkImport('tiendanube')}
                                                style={{ width: '100%', padding: '12px', backgroundColor: '#eab308', color: '#1e293b', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                                            >
                                                🔗 Mapear Productos
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            onClick={handleConnectTiendanube}
                                            style={{ width: '100%', padding: '12px', backgroundColor: '#1A1A1A', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer' }}
                                        >
                                            Vincular con Tiendanube
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Mercado Libre */}
                        {isMLPlatformEnabled && (
                            <div style={{
                                border: '1px solid #e0e0e0',
                                borderRadius: '12px',
                                padding: '24px',
                                backgroundColor: '#fff',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                                    <div style={{
                                        width: '48px',
                                        height: '48px',
                                        borderRadius: '10px',
                                        background: 'linear-gradient(135deg, #FFE600 0%, #FFC700 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '24px',
                                        marginRight: '15px',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}>
                                        🛍️
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize: '18px', fontWeight: '700', margin: 0 }}>Mercado Libre</h3>
                                        <div style={{ fontSize: '12px', marginTop: '2px' }}>
                                            {hasMercadoLibre ? (
                                                <span style={{ color: '#28a745', fontWeight: '600' }}>✓ Conectado</span>
                                            ) : (
                                                <span style={{ color: '#999' }}>No vinculado</span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div style={{ marginBottom: '20px' }}>
                                    <div style={{
                                        padding: '12px',
                                        backgroundColor: '#f8fafc',
                                        borderRadius: '8px',
                                        border: '1px solid #e2e8f0',
                                        marginBottom: '10px'
                                    }}>
                                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            🔄 Opción 1: Stock y Precios
                                        </div>
                                        <p style={{ fontSize: '12px', color: '#64748b', margin: '4px 0 0 24px' }}>
                                            Sincronización automática de stock y precios con tus publicaciones.
                                        </p>
                                    </div>

                                    <div style={{
                                        padding: '12px',
                                        backgroundColor: config.proEnabled !== false ? '#f0fdf4' : '#f8fafc',
                                        borderRadius: '8px',
                                        border: config.proEnabled !== false ? '1px solid #bbf7d0' : '1px solid #e2e8f0'
                                    }}>
                                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            🧾 Opción 2: Facturación Automática
                                            {config.proEnabled === false && <span style={{ fontSize: '10px', backgroundColor: '#e2e8f0', padding: '2px 6px', borderRadius: '4px', marginLeft: 'auto' }}>PRO</span>}
                                        </div>
                                        <p style={{ fontSize: '12px', color: '#64748b', margin: '4px 0 0 24px' }}>
                                            Generación de facturas AFIP y envío automático al chat del comprador.
                                        </p>

                                        {config.proEnabled !== false && (
                                            <div style={{ marginTop: '12px', paddingLeft: '24px' }}>
                                                <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#334155', marginBottom: '4px' }}>
                                                    PUNTO DE VENTA EXCLUSIVO (AFIP)
                                                </label>
                                                <select
                                                    value={defaultPv || ''}
                                                    onChange={(e) => {
                                                        const val = e.target.value ? Number(e.target.value) : null;
                                                        setDefaultPv(val);
                                                        handleUpdateSettings({ ...config.settings, defaultSalesPoint: val });
                                                    }}
                                                    style={{
                                                        width: '100%',
                                                        padding: '8px',
                                                        border: '1px solid #cbd5e1',
                                                        borderRadius: '6px',
                                                        fontSize: '13px',
                                                        backgroundColor: '#fff'
                                                    }}
                                                >
                                                    <option value="">Seleccionar PV...</option>
                                                    {puntosVenta
                                                        .filter(pv => pv.numero !== systemDefaultPv)
                                                        .map(pv => (
                                                            <option key={pv.numero} value={pv.numero}>
                                                                {String(pv.numero).padStart(5, '0')} - {pv.tipo}
                                                            </option>
                                                        ))
                                                    }
                                                </select>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div style={{ marginTop: 'auto' }}>
                                    <div style={{ marginBottom: '16px' }}>
                                        <div style={{ marginBottom: '8px' }}>
                                            <label style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>APP CLIENT ID</label>
                                            <input
                                                type="text"
                                                value={clientId}
                                                onChange={(e) => setClientId(e.target.value)}
                                                placeholder="Ingresa tu ID"
                                                disabled={hasMercadoLibre}
                                                style={{
                                                    width: '100%',
                                                    padding: '8px 12px',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '6px',
                                                    marginTop: '4px',
                                                    fontSize: '14px',
                                                    backgroundColor: hasMercadoLibre ? '#f8f9fa' : '#fff',
                                                    cursor: hasMercadoLibre ? 'not-allowed' : 'text'
                                                }}
                                            />
                                        </div>
                                        <div style={{ marginBottom: '8px' }}>
                                            <label style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>APP CLIENT SECRET</label>
                                            <input
                                                type="password"
                                                value={clientSecret}
                                                onChange={(e) => setClientSecret(e.target.value)}
                                                placeholder={hasMercadoLibre ? '••••••••••••' : 'Ingresa tu Secret'}
                                                disabled={hasMercadoLibre}
                                                style={{
                                                    width: '100%',
                                                    padding: '8px 12px',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '6px',
                                                    marginTop: '4px',
                                                    fontSize: '14px',
                                                    backgroundColor: hasMercadoLibre ? '#f8f9fa' : '#fff',
                                                    cursor: hasMercadoLibre ? 'not-allowed' : 'text'
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {hasMercadoLibre ? (
                                        <>
                                            <button
                                                onClick={handleDisconnectMercadoLibre}
                                                style={{ width: '100%', padding: '12px', backgroundColor: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', marginBottom: '10px' }}
                                            >
                                                Desconectar Cuenta
                                            </button>
                                            <button
                                                onClick={() => handleBulkImport('mercadolibre')}
                                                style={{ width: '100%', padding: '12px', backgroundColor: '#eab308', color: '#1e293b', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                                            >
                                                🔗 Mapear Productos
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            onClick={handleConnectMercadoLibre}
                                            style={{ width: '100%', padding: '12px', backgroundColor: '#FFE600', color: '#000', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer' }}
                                        >
                                            Vincular con Mercado Libre
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Tienda Propia */}
                        {isOwnShopEnabled && (
                            <div style={{
                                border: '1px solid #e0e0e0',
                                borderRadius: '12px',
                                padding: '24px',
                                backgroundColor: '#fff',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                                    <div style={{
                                        width: '48px',
                                        height: '48px',
                                        borderRadius: '10px',
                                        background: 'linear-gradient(135deg, #4F46E5 0%, #3730A3 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '24px',
                                        marginRight: '15px'
                                    }}>
                                        🌐
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize: '18px', fontWeight: '700', margin: 0 }}>Tienda Propia</h3>
                                        <div style={{ fontSize: '12px', color: '#28a745', fontWeight: '600', marginTop: '2px' }}>✓ Activa</div>
                                    </div>
                                </div>

                                <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '20px' }}>
                                    Tu escaparate digital completo. Vende sin comisiones con gestión de envíos, pagos y WhatsApp.
                                </p>

                                <div style={{ backgroundColor: '#f8fafc', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>LINK DE TU TIENDA:</div>
                                    <div style={{ fontSize: '13px', color: '#334155', fontFamily: 'monospace', background: '#fff', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0', wordBreak: 'break-all' }}>
                                        {window.location.origin}/shop/{tenant?.id || tenant?._id}
                                    </div>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: 'auto' }}>
                                    <button
                                        onClick={() => window.open(`/shop/${tenant?.id || tenant?._id}`, '_blank')}
                                        style={{ width: '100%', padding: '12px', backgroundColor: '#4F46E5', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                                    >
                                        Ver mi Tienda Online 🚀
                                    </button>
                                    <button
                                        onClick={() => setShowOwnShopConfig(!showOwnShopConfig)}
                                        style={{ width: '100%', padding: '10px', backgroundColor: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: '8px', fontWeight: '500', cursor: 'pointer' }}
                                    >
                                        {showOwnShopConfig ? 'Cerrar Ajustes' : '⚙️ Gestionar Configuración'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                
                        {/* Editor de Tienda Propia */}
                        {showOwnShopConfig && (
                            <div style={{
                                marginTop: '20px',
                                padding: '30px',
                                backgroundColor: '#fff',
                                borderRadius: '12px',
                                border: '1px solid #e0e0e0',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                            }}>
                                <h2 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '25px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    🛠️ Ajustes de Tienda Propia
                                </h2>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '40px' }}>
                                    {/* WhatsApp y Dominios */}
                                    <div>
                                        <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '15px' }}>General y Contacto</h3>
                                        <label style={{ display: 'block', fontSize: '13px', marginBottom: '8px' }}>WhatsApp (para recibir pedidos)</label>
                                        <input
                                            type="text"
                                            value={ownShopSettings.whatsapp}
                                            onChange={(e) => setOwnShopSettings({ ...ownShopSettings, whatsapp: e.target.value })}
                                            placeholder="Ej: 5491122334455"
                                            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd', marginBottom: '20px' }}
                                        />

                                        <label style={{ display: 'block', fontSize: '13px', marginBottom: '8px' }}>Dirección para mostrar en tienda</label>
                                        <input
                                            type="text"
                                            value={ownShopSettings.address}
                                            onChange={(e) => setOwnShopSettings({ ...ownShopSettings, address: e.target.value })}
                                            placeholder="Ej: Av. Principal 123, CABA"
                                            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd', marginBottom: '20px' }}
                                        />

                                        <label style={{ display: 'block', fontSize: '13px', marginBottom: '8px' }}>Teléfono para mostrar en tienda</label>
                                        <input
                                            type="text"
                                            value={ownShopSettings.phone}
                                            onChange={(e) => setOwnShopSettings({ ...ownShopSettings, phone: e.target.value })}
                                            placeholder="Ej: 011 4567-8901"
                                            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd', marginBottom: '20px' }}
                                        />

                                        <label style={{ display: 'block', fontSize: '13px', marginBottom: '8px' }}>Horarios de Atención</label>
                                        <textarea
                                            value={ownShopSettings.businessHours}
                                            onChange={(e) => setOwnShopSettings({ ...ownShopSettings, businessHours: e.target.value })}
                                            placeholder="Ej: Lunes a Viernes: 09:00 - 18:00&#10;Sábados: 09:00 - 13:00"
                                            rows={3}
                                            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd', marginBottom: '20px', resize: 'vertical', fontSize: '14px' }}
                                        />

                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '20px', padding: '10px', background: '#f8f9fa', borderRadius: '6px' }}>
                                            <input
                                                type="checkbox"
                                                checked={ownShopSettings.showOutOfStock}
                                                onChange={(e) => setOwnShopSettings({ ...ownShopSettings, showOutOfStock: e.target.checked })}
                                            />
                                            <span style={{ fontSize: '13px' }}>Mostrar productos sin stock (aparecerán como "Agotado")</span>
                                        </label>

                                        <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '10px', marginTop: '10px' }}>🌐 Dominios y Acceso</h3>

                                        <div style={{ padding: '15px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                                            <label style={{ display: 'block', fontSize: '12px', color: '#64748b', fontWeight: '600', marginBottom: '4px', textTransform: 'uppercase' }}>Subdominio del sistema</label>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <div style={{ position: 'relative', flex: 1 }}>
                                                    <input
                                                        type="text"
                                                        value={ownShopSettings.subdomain}
                                                        onChange={(e) => setOwnShopSettings({ ...ownShopSettings, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                                                        placeholder="nombre-de-tu-tienda"
                                                        style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }}
                                                    />
                                                    <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '13px' }}>
                                                        .ventio.com.ar
                                                    </span>
                                                </div>
                                                <a href={`/shop/${ownShopSettings.subdomain}`} target="_blank" rel="noreferrer" style={{ fontSize: '12px', color: '#4F46E5', textDecoration: 'none', whiteSpace: 'nowrap' }}>Ver tienda ↗</a>
                                            </div>
                                            <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px' }}>Es tu link gratuito. Usá letras minúsculas, números y guiones.</p>
                                        </div>

                                        <label style={{ display: 'block', fontSize: '13px', marginBottom: '8px' }}>Dominio Propio (Opcional)</label>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <input
                                                type="text"
                                                value={ownShopSettings.customDomain}
                                                onChange={(e) => setOwnShopSettings({ ...ownShopSettings, customDomain: e.target.value })}
                                                placeholder="Ej: tienda.miempresa.com"
                                                style={{ flex: 1, padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
                                            />
                                            {ownShopSettings.customDomain && (
                                                <div style={{
                                                    display: 'flex', alignItems: 'center', gap: '4px', padding: '0 10px',
                                                    borderRadius: '6px', fontSize: '11px',
                                                    background: ownShopSettings.domainVerified ? '#ecfdf5' : '#fff7ed',
                                                    color: ownShopSettings.domainVerified ? '#059669' : '#d97706',
                                                    border: `1px solid ${ownShopSettings.domainVerified ? '#10b981' : '#fbbf24'}`
                                                }}>
                                                    {ownShopSettings.domainVerified ? '✓ Verificado' : '⚠ Pendiente'}
                                                </div>
                                            )}
                                        </div>
                                        {ownShopSettings.customDomain && !ownShopSettings.domainVerified && (
                                            <div style={{ marginTop: '10px', padding: '10px', background: '#fffbeb', borderRadius: '6px', border: '1px solid #fef3c7', fontSize: '12px' }}>
                                                <strong>Instrucciones DNS:</strong> Crea un registro CNAME en tu proveedor (GoDaddy, DonWeb, etc) que apunte su dominio a <code>cname.ventio.com.ar</code>
                                            </div>
                                        )}
                                    </div>

                                    {/* Envíos */}
                                    <div style={{ marginBottom: '25px', padding: '15px', background: '#fff', borderRadius: '8px', border: '1px solid #eee' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                            <h3 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>📦 Envío a Domicilio</h3>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={ownShopSettings.deliveryEnabled}
                                                    onChange={(e) => setOwnShopSettings({ ...ownShopSettings, deliveryEnabled: e.target.checked })}
                                                />
                                                {ownShopSettings.deliveryEnabled ? 'Habilitado' : 'Deshabilitado'}
                                            </label>
                                        </div>

                                        {ownShopSettings.deliveryEnabled && (
                                            <>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                                    <span style={{ fontSize: '13px', color: '#666' }}>Zonas y Costos</span>
                                                    <button
                                                        onClick={() => setOwnShopSettings({
                                                            ...ownShopSettings,
                                                            shippingZones: [...ownShopSettings.shippingZones, { name: '', cost: 0, minPurchase: 0 }]
                                                        })}
                                                        style={{ fontSize: '12px', padding: '4px 8px', background: '#4F46E5', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                                    >+ Añadir Zona</button>
                                                </div>
                                                {ownShopSettings.shippingZones.map((zone, idx) => (
                                                    <div key={idx} style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center' }}>
                                                        <input
                                                            placeholder="Zona"
                                                            value={zone.name}
                                                            onChange={(e) => {
                                                                const newZones = [...ownShopSettings.shippingZones];
                                                                newZones[idx].name = e.target.value;
                                                                setOwnShopSettings({ ...ownShopSettings, shippingZones: newZones });
                                                            }}
                                                            style={{ flex: 2, padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                                                        />
                                                        <input
                                                            type="number"
                                                            placeholder="$ Costo"
                                                            value={zone.cost}
                                                            onChange={(e) => {
                                                                const newZones = [...ownShopSettings.shippingZones];
                                                                newZones[idx].cost = Number(e.target.value);
                                                                setOwnShopSettings({ ...ownShopSettings, shippingZones: newZones });
                                                            }}
                                                            style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                                                        />
                                                        <button
                                                            onClick={() => {
                                                                const newZones = ownShopSettings.shippingZones.filter((_, i) => i !== idx);
                                                                setOwnShopSettings({ ...ownShopSettings, shippingZones: newZones });
                                                            }}
                                                            style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer' }}
                                                        >✕</button>
                                                    </div>
                                                ))}
                                            </>
                                        )}
                                    </div>

                                    {/* Retiro */}
                                    <div style={{ marginBottom: '25px', padding: '15px', background: '#fff', borderRadius: '8px', border: '1px solid #eee' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                            <h3 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>🏠 Retiro en Tienda</h3>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={ownShopSettings.pickupEnabled}
                                                    onChange={(e) => setOwnShopSettings({ ...ownShopSettings, pickupEnabled: e.target.checked })}
                                                />
                                                {ownShopSettings.pickupEnabled ? 'Habilitado' : 'Deshabilitado'}
                                            </label>
                                        </div>

                                        {ownShopSettings.pickupEnabled && (
                                            <>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                                    <span style={{ fontSize: '13px', color: '#666' }}>Puntos de Retiro</span>
                                                    <button
                                                        onClick={() => setOwnShopSettings({
                                                            ...ownShopSettings,
                                                            pickupPoints: [...ownShopSettings.pickupPoints, { name: '', address: '', notes: '' }]
                                                        })}
                                                        style={{ fontSize: '12px', padding: '4px 8px', background: '#4F46E5', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                                    >+ Añadir Punto</button>
                                                </div>
                                                {ownShopSettings.pickupPoints.map((point, idx) => (
                                                    <div key={idx} style={{ marginBottom: '15px', padding: '10px', background: '#f8fafc', borderRadius: '8px', position: 'relative' }}>
                                                        <input
                                                            placeholder="Nombre Local/Punto"
                                                            value={point.name}
                                                            onChange={(e) => {
                                                                const newPoints = [...ownShopSettings.pickupPoints];
                                                                newPoints[idx].name = e.target.value;
                                                                setOwnShopSettings({ ...ownShopSettings, pickupPoints: newPoints });
                                                            }}
                                                            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', marginBottom: '5px' }}
                                                        />
                                                        <input
                                                            placeholder="Dirección Completa"
                                                            value={point.address}
                                                            onChange={(e) => {
                                                                const newPoints = [...ownShopSettings.pickupPoints];
                                                                newPoints[idx].address = e.target.value;
                                                                setOwnShopSettings({ ...ownShopSettings, pickupPoints: newPoints });
                                                            }}
                                                            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                                                        />
                                                        <button
                                                            onClick={() => {
                                                                const newPoints = ownShopSettings.pickupPoints.filter((_, i) => i !== idx);
                                                                setOwnShopSettings({ ...ownShopSettings, pickupPoints: newPoints });
                                                            }}
                                                            style={{ position: 'absolute', top: '5px', right: '5px', background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer' }}
                                                        >✕</button>
                                                    </div>
                                                ))}
                                            </>
                                        )}
                                    </div>

                                    {/* Mercado Pago */}
                                    <div>
                                        <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '15px' }}>💳 Mercado Pago (Cobro Online)</h3>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={ownShopSettings.mercadoPago.enabled}
                                                onChange={(e) => setOwnShopSettings({
                                                    ...ownShopSettings,
                                                    mercadoPago: { ...ownShopSettings.mercadoPago, enabled: e.target.checked }
                                                })}
                                            />
                                            <span>Habilitar cobro automático</span>
                                        </label>

                                        <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>Access Token (Personal o Producción)</label>
                                        <input
                                            type="password"
                                            value={ownShopSettings.mercadoPago.accessToken}
                                            onChange={(e) => setOwnShopSettings({
                                                ...ownShopSettings,
                                                mercadoPago: { ...ownShopSettings.mercadoPago, accessToken: e.target.value }
                                            })}
                                            placeholder="APP_USR-..."
                                            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', marginBottom: '10px' }}
                                        />

                                        <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>Public Key</label>
                                        <input
                                            type="text"
                                            value={ownShopSettings.mercadoPago.publicKey}
                                            onChange={(e) => setOwnShopSettings({
                                                ...ownShopSettings,
                                                mercadoPago: { ...ownShopSettings.mercadoPago, publicKey: e.target.value }
                                            })}
                                            placeholder="APP_USR-..."
                                            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                                        />
                                    </div>
                                </div>

                                <div style={{ marginTop: '30px', borderTop: '1px solid #eee', paddingTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                                    <button
                                        onClick={handleUpdateOwnShop}
                                        style={{ padding: '12px 30px', background: '#111827', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}
                                    >Guardar Cambios</button>
                                </div>
                            </div>
                        )}

                {/* Mapeos de Productos */}
                {( (isMLPlatformEnabled && hasMercadoLibre) || (hasTiendanube) ) && (
                    <div style={{ marginBottom: '30px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h2 style={{ fontSize: '20px', fontWeight: '600', margin: 0 }}>
                                Productos Vinculados ({mappings.length})
                            </h2>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                {hasMercadoLibre && (
                                    <button
                                        onClick={() => handleBulkImport('mercadolibre')}
                                        style={{
                                            padding: '10px 20px',
                                            backgroundColor: '#28a745',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        📥 Importar desde ML
                                    </button>
                                )}
                                {hasTiendanube && (
                                    <button
                                        onClick={() => handleBulkImport('tiendanube')}
                                        style={{
                                            padding: '10px 20px',
                                            backgroundColor: '#eab308',
                                            color: '#1e293b',
                                            border: 'none',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        ☁️ Importar desde Tiendanube
                                    </button>
                                )}
                                {hasTiendanube && (
                                    <button
                                        onClick={() => handleAutoMatch('tiendanube')}
                                        disabled={autoMatching}
                                        style={{
                                            padding: '10px 20px',
                                            backgroundColor: '#3b82f6',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            cursor: autoMatching ? 'not-allowed' : 'pointer',
                                            boxShadow: '0 4px 6px rgba(59, 130, 246, 0.2)',
                                            transition: 'all 0.2s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        {autoMatching ? '⏳ Procesando...' : '⚡ Auto-Mapear Tiendanube'}
                                    </button>
                                )}
                                {hasMercadoLibre && (
                                    <button
                                        onClick={() => handleAutoMatch('mercadolibre')}
                                        disabled={autoMatching}
                                        style={{
                                            padding: '10px 20px',
                                            backgroundColor: '#0284c7',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            cursor: autoMatching ? 'not-allowed' : 'pointer',
                                            boxShadow: '0 4px 6px rgba(2, 132, 199, 0.2)',
                                            transition: 'all 0.2s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        {autoMatching ? '⏳ Procesando...' : '⚡ Auto-Mapear ML'}
                                    </button>
                                )}
                                <button
                                    onClick={() => {
                                        setMappingPlatform(hasMercadoLibre ? 'mercadolibre' : 'tiendanube');
                                        setShowMappingModal(true);
                                    }}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#007bff',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        cursor: 'pointer'
                                    }}
                                >
                                    + Vincular Producto
                                </button>
                            </div>
                        </div>

                        {mappings.length === 0 ? (
                            <div style={{
                                border: '2px dashed #ddd',
                                borderRadius: '8px',
                                padding: '40px',
                                textAlign: 'center',
                                color: '#999'
                            }}>
                                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
                                <div style={{ fontSize: '16px', marginBottom: '8px' }}>No hay productos vinculados</div>
                                <div style={{ fontSize: '14px' }}>Comienza vinculando tus productos con publicaciones de Mercado Libre</div>
                            </div>
                        ) : (
                            <div style={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
                                            <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Producto Local</th>
                                            <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>ID Externo</th>
                                            <th style={{ padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: '600' }}>Plataforma</th>
                                            <th style={{ padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: '600' }}>Stock</th>
                                            <th style={{ padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: '600' }}>Última Sync</th>
                                            <th style={{ padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: '600' }}>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {mappings.map((mapping) => (
                                            <tr key={mapping._id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                                <td style={{ padding: '12px' }}>
                                                    <div style={{ fontWeight: '500' }}>{mapping.productId?.name || 'Producto eliminado'}</div>
                                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                                        {mapping.productId?.barcode || mapping.productId?.sku || '-'}
                                                    </div>
                                                </td>
                                                <td style={{ padding: '12px', fontSize: '14px', fontFamily: 'monospace' }}>
                                                    {mapping.externalId}
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                                    <span style={{
                                                        padding: '4px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '12px',
                                                        fontWeight: '600',
                                                        backgroundColor: mapping.platform === 'mercadolibre' ? '#fff3cd' : '#f1f5f9',
                                                        color: mapping.platform === 'mercadolibre' ? '#856404' : '#475569',
                                                        border: `1px solid ${mapping.platform === 'mercadolibre' ? '#ffeeba' : '#cbd5e1'}`
                                                    }}>
                                                        {mapping.platform === 'mercadolibre' ? 'MercadoLibre' : 'Tiendanube'}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: '600' }}>
                                                    {mapping.productId?.stock ?? '-'}
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'center', fontSize: '12px', color: '#666' }}>
                                                    {mapping.lastSyncAt ? new Date(mapping.lastSyncAt).toLocaleString('es-AR') : 'Nunca'}
                                                    {mapping.lastSyncStatus === 'success' && <div style={{ color: '#28a745' }}>✓ OK</div>}
                                                    {mapping.lastSyncStatus === 'error' && <div style={{ color: '#dc3545' }}>✗ Error</div>}
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                                        <button
                                                            onClick={() => handleSyncStock(mapping.productId._id, mapping.platform)}
                                                            disabled={syncing}
                                                            style={{
                                                                padding: '6px 12px',
                                                                backgroundColor: '#28a745',
                                                                color: '#fff',
                                                                border: 'none',
                                                                borderRadius: '4px',
                                                                fontSize: '12px',
                                                                cursor: syncing ? 'not-allowed' : 'pointer',
                                                                opacity: syncing ? 0.6 : 1
                                                            }}
                                                        >
                                                            🔄 Stock
                                                        </button>
                                                        <button
                                                            onClick={() => handleSyncPrice(mapping.productId._id, mapping.platform)}
                                                            disabled={syncing}
                                                            style={{
                                                                padding: '6px 12px',
                                                                backgroundColor: '#ffc107',
                                                                color: '#000',
                                                                border: 'none',
                                                                borderRadius: '4px',
                                                                fontSize: '12px',
                                                                cursor: syncing ? 'not-allowed' : 'pointer',
                                                                opacity: syncing ? 0.6 : 1
                                                            }}
                                                        >
                                                            💰 Precio
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteMapping(mapping._id)}
                                                            style={{
                                                                padding: '6px 12px',
                                                                backgroundColor: '#dc3545',
                                                                color: '#fff',
                                                                border: 'none',
                                                                borderRadius: '4px',
                                                                fontSize: '12px',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            ✗
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* Configuración */}
                {((isMLPlatformEnabled && hasMercadoLibre) || hasTiendanube) && config.settings && (
                    <div style={{ marginBottom: '30px' }}>
                        <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px' }}>
                            Configuración de Sincronización
                        </h2>

                        <div style={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '20px' }}>
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={config.settings.syncStock}
                                        onChange={(e) => handleUpdateSettings({ ...config.settings, syncStock: e.target.checked })}
                                        style={{ marginRight: '8px' }}
                                    />
                                    <span style={{ fontSize: '14px' }}>Sincronizar stock automáticamente</span>
                                </label>
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={config.settings.syncPrice}
                                        onChange={(e) => handleUpdateSettings({ ...config.settings, syncPrice: e.target.checked })}
                                        style={{ marginRight: '8px' }}
                                    />
                                    <span style={{ fontSize: '14px' }}>Sincronizar precios automáticamente</span>
                                </label>
                            </div>

                            {config.proEnabled !== false && (
                                <>
                                    <div style={{ marginBottom: '16px' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={config.settings.autoInvoiceEnabled || false}
                                                onChange={(e) => handleUpdateSettings({ ...config.settings, autoInvoiceEnabled: e.target.checked })}
                                                style={{ marginRight: '8px' }}
                                            />
                                            <span style={{ fontSize: '14px' }}>Facturar ventas automáticamente en AFIP (PRO)</span>
                                        </label>
                                    </div>

                                    {config.settings.autoInvoiceEnabled && hasMercadoLibre && (
                                        <div style={{ marginBottom: '16px', marginLeft: '24px' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={config.settings.autoSendInvoice !== false}
                                                    onChange={(e) => handleUpdateSettings({ ...config.settings, autoSendInvoice: e.target.checked })}
                                                    style={{ marginRight: '8px' }}
                                                />
                                                <span style={{ fontSize: '14px' }}>Enviar factura automáticamente a ML (Fiscal + Chat)</span>
                                            </label>
                                        </div>
                                    )}
                                </>
                            )}

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '14px', marginBottom: '8px' }}>
                                    Recargo de precio para e-commerce (%)
                                </label>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input
                                        type="number"
                                        value={priceMarkup}
                                        onChange={(e) => setPriceMarkup(parseFloat(e.target.value) || 0)}
                                        style={{
                                            width: '200px',
                                            padding: '8px 12px',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            fontSize: '14px'
                                        }}
                                        min="0"
                                        max="100"
                                        step="0.1"
                                    />
                                    <button
                                        onClick={() => handleUpdateSettings({ ...config.settings, priceMarkupPercentage: priceMarkup })}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: '#007bff',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '4px',
                                            fontSize: '14px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Guardar
                                    </button>
                                </div>
                                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                    Ejemplo: 10% para cubrir comisiones de la plataforma
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal de Mapeo */}
                {showMappingModal && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}>
                        <div style={{
                            backgroundColor: '#fff',
                            borderRadius: '12px',
                            padding: '30px',
                            width: '500px',
                            boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
                        }}>
                            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>
                                Vincular Producto con {mappingPlatform === 'mercadolibre' ? 'Mercado Libre' : 'Tiendanube'}
                            </h3>

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '14px', marginBottom: '8px' }}>
                                    Buscar Producto Local
                                </label>
                                <select
                                    value={selectedProduct}
                                    onChange={(e) => setSelectedProduct(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '6px',
                                        fontSize: '14px'
                                    }}
                                >
                                    <option value="">Seleccionar producto...</option>
                                    {products.map((product) => (
                                        <option key={product._id} value={product._id}>
                                            {product.name} ({product.barcode || product.sku || 'S/C'})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div style={{ marginBottom: '24px' }}>
                                <label style={{ display: 'block', fontSize: '14px', marginBottom: '8px' }}>
                                    ID de {mappingPlatform === 'mercadolibre' ? 'Publicación de ML' : 'Producto en Tiendanube'}
                                </label>
                                <input
                                    type="text"
                                    value={externalId}
                                    onChange={(e) => setExternalId(e.target.value)}
                                    placeholder={mappingPlatform === 'mercadolibre' ? 'Ej: MLA123456789' : 'Ej: 1234567'}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '6px',
                                        fontSize: '14px'
                                    }}
                                />
                            </div>

                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                <button
                                    onClick={() => setShowMappingModal(false)}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#f8f9fa',
                                        color: '#333',
                                        border: '1px solid #ddd',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleCreateMapping}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#007bff',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Vincular
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal de Importación Masiva */}
                {showBulkImportModal && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}>
                        <div style={{
                            backgroundColor: '#fff',
                            borderRadius: '12px',
                            padding: '30px',
                            width: '90%',
                            maxWidth: '1000px',
                            maxHeight: '80vh',
                            overflowY: 'auto',
                            boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                <h3 style={{ fontSize: '20px', fontWeight: '600', margin: 0 }}>
                                    Vincular Productos de {mappingPlatform === 'mercadolibre' ? 'Mercado Libre' : 'Tiendanube'}
                                </h3>
                                <button
                                    onClick={() => setShowBulkImportModal(false)}
                                    style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}
                                >
                                    ×
                                </button>
                            </div>

                            {loadingMlItems ? (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    Cargando productos de la plataforma...
                                </div>
                            ) : (
                                <>
                                    <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
                                        Hemos encontrado {externalItems.length} productos en tu cuenta. Selecciona los que quieras vincular con tu inventario local.
                                    </p>

                                    <datalist id="local-products-list">
                                        {products.map((p) => (
                                            <option key={p._id} value={`${p.name} (${p.barcode || p.sku || 'S/C'})`}>
                                                {p._id}
                                            </option>
                                        ))}
                                    </datalist>

                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
                                                <th style={{ padding: '12px', textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setBulkMappings(externalItems.map(item => ({
                                                                    externalId: item.id,
                                                                    title: item.title,
                                                                    productId: ''
                                                                })));
                                                            } else {
                                                                setBulkMappings([]);
                                                            }
                                                        }}
                                                    />
                                                </th>
                                                <th style={{ padding: '12px', textAlign: 'left' }}>Producto Externo</th>
                                                <th style={{ padding: '12px', textAlign: 'left' }}>Vincular con Producto Local</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {externalItems.map((item) => (
                                                <tr key={item.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                                    <td style={{ padding: '12px', textAlign: 'center' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={bulkMappings.some(m => m.externalId === item.id)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setBulkMappings([...bulkMappings, { externalId: item.id, title: item.title, productId: '' }]);
                                                                } else {
                                                                    setBulkMappings(bulkMappings.filter(m => m.externalId !== item.id));
                                                                }
                                                            }}
                                                        />
                                                    </td>
                                                    <td style={{ padding: '12px' }}>
                                                        <div style={{ fontWeight: '500' }}>{item.title}</div>
                                                        <div style={{ fontSize: '12px', color: '#999', fontFamily: 'monospace' }}>{item.id}</div>
                                                    </td>
                                                    <td style={{ padding: '12px' }}>
                                                        <input
                                                            type="text"
                                                            list="local-products-list"
                                                            placeholder="Escribe para buscar..."
                                                            value={
                                                                rowSearch[item.id] !== undefined 
                                                                ? rowSearch[item.id] 
                                                                : (products.find(p => p._id === bulkMappings.find(m => m.externalId === item.id)?.productId)?.name 
                                                                    ? `${products.find(p => p._id === bulkMappings.find(m => m.externalId === item.id)?.productId).name} (${products.find(p => p._id === bulkMappings.find(m => m.externalId === item.id)?.productId).barcode || products.find(p => p._id === bulkMappings.find(m => m.externalId === item.id)?.productId).sku || 'S/C'})`
                                                                    : '')
                                                            }
                                                            onChange={(e) => {
                                                                const val = e.target.value;
                                                                setRowSearch(prev => ({ ...prev, [item.id]: val }));
                                                                
                                                                // Intentar encontrar coincidencia exacta para vincular
                                                                const matchedProduct = products.find(p => `${p.name} (${p.barcode || p.sku || 'S/C'})` === val);
                                                                
                                                                if (matchedProduct) {
                                                                    const newMappings = bulkMappings.map(m =>
                                                                        m.externalId === item.id ? { ...m, productId: matchedProduct._id } : m
                                                                    );
                                                                    setBulkMappings(newMappings);
                                                                } else if (val === '') {
                                                                    const newMappings = bulkMappings.map(m =>
                                                                        m.externalId === item.id ? { ...m, productId: '' } : m
                                                                    );
                                                                    setBulkMappings(newMappings);
                                                                }
                                                            }}
                                                            disabled={!bulkMappings.some(m => m.externalId === item.id)}
                                                            style={{
                                                                width: '100%',
                                                                padding: '8px',
                                                                border: '1px solid #ddd',
                                                                borderRadius: '4px',
                                                                fontSize: '13px'
                                                            }}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                        <button
                                            onClick={() => setShowBulkImportModal(false)}
                                            style={{
                                                padding: '10px 20px',
                                                backgroundColor: '#f8f9fa',
                                                color: '#333',
                                                border: '1px solid #ddd',
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            onClick={handleSaveBulkMappings}
                                            disabled={bulkMappings.length === 0}
                                            style={{
                                                padding: '10px 20px',
                                                backgroundColor: '#28a745',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                fontWeight: '500',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Vincular Seleccionados
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* Modal de Reporte de Mapeo Automático */}
                {showAutoMatchModal && autoMatchReport && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(15, 23, 42, 0.65)',
                        backdropFilter: 'blur(8px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1100
                    }}>
                        <div style={{
                            backgroundColor: '#ffffff',
                            borderRadius: '16px',
                            padding: '32px',
                            width: '90%',
                            maxWidth: '650px',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                            maxHeight: '85vh',
                            display: 'flex',
                            flexDirection: 'column',
                            fontFamily: "'Inter', sans-serif"
                        }}>
                            {/* Encabezado */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                                <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    ⚡ Mapeo Automático Express
                                </h3>
                                <button
                                    onClick={() => setShowAutoMatchModal(false)}
                                    style={{
                                        background: '#f1f5f9',
                                        border: 'none',
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '50%',
                                        fontSize: '18px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#64748b'
                                    }}
                                >
                                    ×
                                </button>
                            </div>

                            {/* Contenido / Métricas */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                                <div style={{ backgroundColor: '#f0fdf4', borderRadius: '12px', padding: '16px', textAlign: 'center', border: '1px solid #bbf7d0' }}>
                                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#166534', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Nuevos Vínculos</div>
                                    <div style={{ fontSize: '28px', fontWeight: '800', color: '#15803d', marginTop: '4px' }}>{autoMatchReport.createdCount}</div>
                                </div>
                                <div style={{ backgroundColor: '#f8fafc', borderRadius: '12px', padding: '16px', textAlign: 'center', border: '1px solid #e2e8f0' }}>
                                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Ya Vinculados</div>
                                    <div style={{ fontSize: '28px', fontWeight: '800', color: '#334155', marginTop: '4px' }}>{autoMatchReport.matchedCount - autoMatchReport.createdCount}</div>
                                </div>
                                <div style={{ backgroundColor: '#fef2f2', borderRadius: '12px', padding: '16px', textAlign: 'center', border: '1px solid #fee2e2' }}>
                                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#991b1b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Errores</div>
                                    <div style={{ fontSize: '28px', fontWeight: '800', color: '#b91c1c', marginTop: '4px' }}>{autoMatchReport.errors?.length || 0}</div>
                                </div>
                            </div>

                            {/* Detalle de Productos */}
                            <div style={{ flex: 1, overflowY: 'auto', marginBottom: '24px', paddingRight: '4px' }}>
                                <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#475569', marginBottom: '12px' }}>Detalles del Proceso</h4>
                                
                                {autoMatchReport.matchedDetails?.length === 0 && autoMatchReport.errors?.length === 0 ? (
                                    <div style={{ padding: '24px', textAlign: 'center', color: '#64748b', fontSize: '14px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                                        No se encontraron productos coincidentes por SKU o Código de Barras que no estuvieran ya vinculados.
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {autoMatchReport.matchedDetails?.map((detail, idx) => (
                                            <div key={idx} style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '12px 16px',
                                                backgroundColor: '#f8fafc',
                                                borderRadius: '8px',
                                                borderLeft: detail.status === 'created' ? '4px solid #22c55e' : '4px solid #94a3b8'
                                            }}>
                                                <div>
                                                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{detail.productName}</div>
                                                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>SKU/Código: <strong>{detail.sku}</strong> | ID Externo: {detail.externalId}</div>
                                                </div>
                                                <span style={{
                                                    fontSize: '11px',
                                                    fontWeight: '700',
                                                    padding: '4px 8px',
                                                    borderRadius: '100px',
                                                    textTransform: 'uppercase',
                                                    backgroundColor: detail.status === 'created' ? '#dcfce7' : '#f1f5f9',
                                                    color: detail.status === 'created' ? '#15803d' : '#475569'
                                                }}>
                                                    {detail.status === 'created' ? 'Nuevo Vínculo' : 'Ya Vinculado'}
                                                </span>
                                            </div>
                                        ))}

                                        {autoMatchReport.errors?.map((err, idx) => (
                                            <div key={`err-${idx}`} style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '12px 16px',
                                                backgroundColor: '#fef2f2',
                                                borderRadius: '8px',
                                                borderLeft: '4px solid #ef4444'
                                            }}>
                                                <div>
                                                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#991b1b' }}>{err.productName}</div>
                                                    <div style={{ fontSize: '12px', color: '#b91c1c', marginTop: '2px' }}>{err.error}</div>
                                                </div>
                                                <span style={{
                                                    fontSize: '11px',
                                                    fontWeight: '700',
                                                    padding: '4px 8px',
                                                    borderRadius: '100px',
                                                    textTransform: 'uppercase',
                                                    backgroundColor: '#fee2e2',
                                                    color: '#b91c1c'
                                                }}>
                                                    Error
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Botón de Cierre */}
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <button
                                    onClick={() => setShowAutoMatchModal(false)}
                                    style={{
                                        padding: '12px 24px',
                                        backgroundColor: '#1e293b',
                                        color: '#ffffff',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                    }}
                                >
                                    ¡Excelente!
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Logs Tab */}
            {activeTab === 'logs' && (
                <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', marginTop: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
                        <div>
                            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#1e293b' }}>📋 Historial de Sincronización</h3>
                            <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#64748b' }}>Monitorea el estado y las operaciones en tiempo real entre Ventio, Tiendanube y Mercado Libre.</p>
                        </div>
                        <button
                            onClick={loadSyncLogs}
                            disabled={logsLoading}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '10px 18px',
                                backgroundColor: '#2196f3',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                opacity: logsLoading ? 0.7 : 1
                            }}
                        >
                            🔄 {logsLoading ? 'Cargando...' : 'Actualizar'}
                        </button>
                    </div>

                    {/* Filtros */}
                    <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap', backgroundColor: '#f8fafc', padding: '16px', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>Plataforma</label>
                            <select
                                value={logsPlatform}
                                onChange={(e) => { setLogsPlatform(e.target.value); setLogsPage(1); }}
                                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', backgroundColor: '#fff', minWidth: '150px' }}
                            >
                                <option value="">Todas</option>
                                <option value="tiendanube">Tiendanube</option>
                                <option value="mercadolibre">Mercado Libre</option>
                            </select>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>Estado</label>
                            <select
                                value={logsStatus}
                                onChange={(e) => { setLogsStatus(e.target.value); setLogsPage(1); }}
                                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', backgroundColor: '#fff', minWidth: '150px' }}
                            >
                                <option value="">Todos</option>
                                <option value="success">Exitosos</option>
                                <option value="error">Con Error</option>
                            </select>
                        </div>
                    </div>

                    {/* Tabla de Logs */}
                    {logsLoading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', flexDirection: 'column', gap: '12px' }}>
                            <div style={{ width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid #2196f3', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                            <span style={{ color: '#64748b', fontSize: '14px' }}>Cargando logs...</span>
                        </div>
                    ) : syncLogs.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '48px 24px', color: '#64748b', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                            <div style={{ fontSize: '32px', marginBottom: '12px' }}>📋</div>
                            <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600', color: '#334155' }}>No se encontraron registros</h4>
                            <p style={{ margin: 0, fontSize: '14px' }}>Aún no hay eventos de sincronización registrados para los filtros seleccionados.</p>
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                        <th style={{ padding: '14px 16px', fontWeight: '600', color: '#475569' }}>Fecha</th>
                                        <th style={{ padding: '14px 16px', fontWeight: '600', color: '#475569' }}>Plataforma</th>
                                        <th style={{ padding: '14px 16px', fontWeight: '600', color: '#475569' }}>Operación</th>
                                        <th style={{ padding: '14px 16px', fontWeight: '600', color: '#475569' }}>Producto / Detalle</th>
                                        <th style={{ padding: '14px 16px', fontWeight: '600', color: '#475569' }}>Estado</th>
                                        <th style={{ padding: '14px 16px', fontWeight: '600', color: '#475569' }}>Mensaje</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {syncLogs.map((log) => {
                                        const dateStr = log.createdAt ? new Date(log.createdAt).toLocaleString('es-AR') : '-';
                                        const platformLabel = log.platform === 'tiendanube' ? '☁️ Tiendanube' : '🛒 Mercado Libre';
                                        
                                        let actionLabel = log.action || '-';
                                        if (log.action === 'sincronizacion_stock') actionLabel = '📦 Sinc. Stock';
                                        if (log.action === 'sincronizacion_precio') actionLabel = '💰 Sinc. Precio';
                                        if (log.action === 'orden_importada') actionLabel = '📥 Import. Orden';

                                        return (
                                            <tr key={log._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '14px 16px', color: '#334155', whiteSpace: 'nowrap' }}>{dateStr}</td>
                                                <td style={{ padding: '14px 16px', fontWeight: '500', color: '#334155' }}>{platformLabel}</td>
                                                <td style={{ padding: '14px 16px', fontWeight: '600', color: '#475569' }}>{actionLabel}</td>
                                                <td style={{ padding: '14px 16px', color: '#334155' }}>
                                                    {log.productName && log.productName !== 'Desconocido' ? (
                                                        <strong>{log.productName}</strong>
                                                    ) : (
                                                        <span style={{ color: '#64748b', fontStyle: 'italic' }}>Global / Orden</span>
                                                    )}
                                                </td>
                                                <td style={{ padding: '14px 16px' }}>
                                                    <span style={{
                                                        padding: '4px 8px',
                                                        borderRadius: '100px',
                                                        fontSize: '12px',
                                                        fontWeight: '700',
                                                        textTransform: 'uppercase',
                                                        backgroundColor: log.status === 'success' ? '#dcfce7' : '#fee2e2',
                                                        color: log.status === 'success' ? '#166534' : '#991b1b'
                                                    }}>
                                                        {log.status === 'success' ? 'Éxito' : 'Error'}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '14px 16px', color: log.status === 'success' ? '#475569' : '#b91c1c' }}>
                                                    <div style={{ maxWidth: '350px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={log.details || log.errorMessage}>
                                                        {log.status === 'success' ? log.details : (log.errorMessage || log.details || 'Fallo desconocido')}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Paginación */}
                    {syncLogs.length > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px' }}>
                            <span style={{ fontSize: '14px', color: '#64748b' }}>
                                Página <strong>{logsPage}</strong> de <strong>{logsTotalPages}</strong>
                            </span>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                    onClick={() => setLogsPage(prev => Math.max(prev - 1, 1))}
                                    disabled={logsPage === 1 || logsLoading}
                                    style={{
                                        padding: '8px 14px',
                                        backgroundColor: '#fff',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '6px',
                                        cursor: logsPage === 1 ? 'not-allowed' : 'pointer',
                                        opacity: logsPage === 1 ? 0.5 : 1,
                                        fontSize: '14px',
                                        fontWeight: '600'
                                    }}
                                >
                                    Anterior
                                </button>
                                <button
                                    onClick={() => setLogsPage(prev => Math.min(prev + 1, logsTotalPages))}
                                    disabled={logsPage === logsTotalPages || logsLoading}
                                    style={{
                                        padding: '8px 14px',
                                        backgroundColor: '#fff',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '6px',
                                        cursor: logsPage === logsTotalPages ? 'not-allowed' : 'pointer',
                                        opacity: logsPage === logsTotalPages ? 0.5 : 1,
                                        fontSize: '14px',
                                        fontWeight: '600'
                                    }}
                                >
                                    Siguiente
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Ecommerce;
